import { Part } from "../interface/part";
import { TimeTracker } from "../interface/time-tracker";
import { signal, computed, WritableSignal, Signal, isSignal } from "@angular/core";
import { ApiOS } from "./mkg-os";
import { Money } from "./money";
import { ApiFloat } from "./api-float";
import moment, { Moment } from "moment";
import { BOOLEAN_TWO } from "../shared/common-enums/boolean-two";

export enum ITEM_BLOCK {
  "Blocked" = 1, // Bloqueado
  "Released" // Liberado
}

export class MkgOSPart {
  id?: string;
  part?: Part; // required
  /**
   * The item cost when inserted into OS
   * 
   * Multiplied by 100 into backend 
   **/
  purchaseValue?: number;
  /**
   * The average cost of item when inserted into OS
   * 
   *  Multiplied by 100 into backend 
   **/
  purchaseAverage?: number;
  /** 
   * Está sendo invertido o campo de observações para que as observações
   * que ja existiam (campo 'observation') aparecam como padrão no pdf  
   */
  observation?: string;
  status?: number;
  internalObservation?: string;
  seq?: number;
  /** 1 - not delivered | 2 - delivered */
  delivery?: BOOLEAN_TWO;
  /** @see partStates */
  available?: number;
  lastUpdate?: Date;
  timeTrackers?: TimeTracker[];
  blocked?: boolean;
  running?: boolean;
  mechanic?: string;
  itenBlock?: number;
  /** 1 - not applied | 2 - applied */
  applied?: BOOLEAN_TWO;

  amount!: WritableSignal<number>;

  /** Multiplied by 100 into backend */
  saleValue!: WritableSignal<number>;

  /** The percentage of gross value of given discount */
  additionalDiscount!: number;

  /** 
   * The additional discount, in currency value 
   * 
   * Multiplied by 100 into backend
   */
  discountValue!: WritableSignal<number>;

  /** 
   * The currency discount equivalent to OS.discountParts
   * 
   * Multiplied by 100 into backend
   */
  discountProp!: WritableSignal<number>;

  /** 
   * The currency discount equivalent to OS.generalDiscount
   *
   * Multiplied by 100 into backend 
   */
  discountPropGeral!: WritableSignal<number>;

  grossValue!: WritableSignal<number>;

  /** grossValue - discountValue */
  liquid1!: WritableSignal<number>;

  /** grossValue - discountValue - discountPropParts */
  liquid2!: WritableSignal<number>;

  /** grossValue - discountValue - discountPropParts - discountPropOS*/
  liquidValue!: WritableSignal<number>;

  // absent in the API
  /** @deprecated */
  oldAmount?: number;
  /** @deprecated */
  oldUnitValue?: number;
  /** @deprecated */
  error?: number;
  
  /** Flag temporária para indicar qual campo de desconto foi alterado */
  private __lastDiscountFieldChanged?: 'additionalDiscount' | 'discountValue';

  nationalFederal?: number
  state?: number;
  municipal?: number;
  key?: string;
  nroPedido?: string;
  seqItem?: number;
  unitOfMeasurements?: string;

  updatedAt?: Moment;
  createdAt?: Moment;

  constructor(apiObj: any, divideByOneHundred = true) {
    apiObj.discountValue = isSignal(apiObj.discountValue) ? apiObj.discountValue() : apiObj.discountValue;
    apiObj.discountProp = isSignal(apiObj.discountProp) ? apiObj.discountProp() : apiObj.discountProp;
    apiObj.discountPropGeral = isSignal(apiObj.discountPropGeral) ? apiObj.discountPropGeral() : apiObj.discountPropGeral;
    apiObj.saleValue = isSignal(apiObj.saleValue) ? apiObj.saleValue() : apiObj.saleValue;

    this.purchaseValue = (apiObj.purchaseValue || 0) / (divideByOneHundred ? 100 : 1);
    this.purchaseAverage = (apiObj.purchaseAverage || 0) / (divideByOneHundred ? 100 : 1);
    this.observation = apiObj.internalObservation ?? "";
    this.internalObservation = apiObj.observation ?? "";
    this.amount = isSignal(apiObj.amount) ? apiObj.amount : signal(apiObj.amount);
    this.saleValue = signal((apiObj.saleValue ?? 0) / (divideByOneHundred ? 100 : 1));
    
    // Calcular valores iniciais
    const initialGrossValue = this.amount() * this.saleValue();
    const initialDiscountValue = (apiObj.discountValue ?? 0) / (divideByOneHundred ? 100 : 1);
    const initialDiscountProp = (apiObj.discountProp ?? 0) / (divideByOneHundred ? 100 : 1);
    const initialDiscountPropGeral = (apiObj.discountPropGeral ?? 0) / (divideByOneHundred ? 100 : 1);
    const initialLiquid1 = initialGrossValue - initialDiscountValue;
    const initialLiquid2 = initialLiquid1 - initialDiscountProp;
    const initialLiquidValue = Money(initialLiquid2 - initialDiscountPropGeral);
    
    // Criar signals com valores calculados (backend é a fonte da verdade)
    this.grossValue = signal(initialGrossValue);
    this.discountValue = signal(initialDiscountValue);
    this.discountProp = signal(initialDiscountProp);
    this.discountPropGeral = signal(initialDiscountPropGeral);
    this.liquid1 = signal(initialLiquid1);
    this.liquid2 = signal(initialLiquid2);
    this.liquidValue = signal(initialLiquidValue);
    this.applied = apiObj.applied ?? BOOLEAN_TWO.FALSE

    if (apiObj.id) {
      this.id = apiObj.id;
    }

    if (apiObj.part) {
      this.part = apiObj.part;

      if (apiObj.part?.unitMeasure) {
        this.part.unitOfMeasurements = apiObj.part.unitMeasure;
      }

      if (apiObj?.part?.location) {
        apiObj.part.allocation = apiObj.part.location;
      }
    }

    if (apiObj.mechanic) {
      this.mechanic = apiObj.mechanic;
    }

    if (apiObj.lastUpdate) {
      this.lastUpdate = new Date(apiObj.lastUpdate);
    }

    if (Array.isArray(apiObj.timeTrackers)) {
      this.timeTrackers = apiObj.timeTrackers;
    } else {
      this.timeTrackers = [];
    }

    if (Number.isFinite(apiObj.status)) {
      this.status = apiObj.status;
    }

    if (Number.isFinite(apiObj.seq)) {
      this.seq = apiObj.seq;
    }

    if (Number.isFinite(apiObj.delivery)) {
      this.delivery = apiObj.delivery;
    }

    if (Number.isFinite(apiObj.available)) {
      this.available = apiObj.available;
    }

    if (Number.isFinite(apiObj.itenBlock)) {
      this.itenBlock = apiObj.itenBlock;
    }

    if (Number.isFinite(apiObj.additionalDiscount)) {
      this.additionalDiscount = apiObj.additionalDiscount
    }

    if (Object.hasOwn(apiObj, "blocked")) {
      this.blocked = apiObj.block;
    }

    if (Object.hasOwn(apiObj, "running")) {
      this.running = apiObj.running;
    }

    if (apiObj.key) {
      this.nationalFederal = apiObj.nationalFederal;
      this.state = apiObj.state;
      this.municipal = apiObj.municipal;
      this.key = apiObj.key;
    }

    if (apiObj.nroPedido) {
      this.nroPedido = apiObj.nroPedido;
    }
    
    if (apiObj.seqItem) {
      this.seqItem = apiObj.seqItem;
    }

    if (apiObj.createdAt) {
      this.createdAt = moment(apiObj.createdAt);
    }

    if (apiObj.updatedAt) {
      this.updatedAt = moment(apiObj.updatedAt);
    }

    if (apiObj.oldAmount !== null && apiObj.oldAmount !== undefined && apiObj.oldAmount !== "") {
      this.oldAmount = apiObj.oldAmount;
    }
  }

  toAPI(): ApiOS['parts'][number] {
    // ✅ CORREÇÃO: Enviar apenas o campo de desconto que foi alterado
    // Isso evita conflitos onde o frontend envia ambos e o backend não sabe qual usar
    const lastDiscountFieldChanged = this['__lastDiscountFieldChanged'];
    
    let apiPart = {
      "amount": ApiFloat(this.amount(), 3),
      "saleValue": ApiFloat(this.saleValue()),
      "purchaseValue": ApiFloat(this.purchaseValue),
      "purchaseAverage": ApiFloat(this.purchaseAverage),
      "delivery": this.delivery ?? 1,
      "applied": this.applied ?? 1,
      "observation": this.internalObservation,
      "internalObservation": this.observation,
      "available": (this.available || "0") as number,
      "lastUpdate": new Date()
    };
    
    // 🎯 ESTRATÉGIA: Enviar apenas o campo de desconto que foi alterado
    if (lastDiscountFieldChanged === 'additionalDiscount') {
      // Usuário alterou o PERCENTUAL → enviar apenas additionalDiscount
      apiPart["additionalDiscount"] = this.additionalDiscount;
      // NÃO enviar discountValue (backend calculará)
    } else if (lastDiscountFieldChanged === 'discountValue') {
      // Usuário alterou o VALOR ABSOLUTO → enviar apenas discountValue
      apiPart["discountValue"] = ApiFloat(this.discountValue());
      // NÃO enviar additionalDiscount (backend calculará)
    } else {
      // Nenhuma alteração recente ou criação inicial → enviar ambos
      apiPart["additionalDiscount"] = this.additionalDiscount;
      apiPart["discountValue"] = ApiFloat(this.discountValue());
    }
    
    // ❌ NÃO enviar valores calculados pelo backend:
    // "discountProp": Calculado pelo backend (desconto proporcional de peças)
    // "discountPropGeral": Calculado pelo backend (desconto geral da OS)
    // "liquidValue": Calculado pelo backend (valor líquido final)
    
    if (this.id) {
      apiPart["id"] = this.id;
    }
    if (this.status) {
      apiPart["status"] = this.status;
    }
    if (this.timeTrackers) {
      apiPart["timeTrackers"] = this.timeTrackers;
    }
    if (this.hasOwnProperty("blocked")) {
      apiPart["blocked"] = this.blocked;
    }
    if (this.hasOwnProperty("running")) {
      apiPart["running"] = this.running;
    }
    if (this.mechanic) {
      apiPart["mechanic"] = this.mechanic;
    } else {
      apiPart["mechanic"] = "";
    }
    if (this.part && this.part.id) {
      apiPart["part"] = this.part.id;
    }
    if (Number.isFinite(this.seq)) {
      apiPart["seq"] = this.seq;
    }
    if (this.itenBlock) {
      apiPart["itenBlock"] = this.itenBlock;
    }

    if (this.nroPedido) {
      apiPart["nroPedido"] = this.nroPedido;
    }
    if (this.seqItem) {
      apiPart["seqItem"] = +this.seqItem;
    }
    
    // Limpar flag após usar
    delete this['__lastDiscountFieldChanged'];

    return apiPart
  }
}